/**
 * COM-3.9 — who may see, approve, pay, export and adjust money.
 *
 * ⚠️ THIS FILE DECIDES NOTHING THE DATABASE DOES NOT ALSO DECIDE.
 *
 * `commission_lines_select` and `commission_has_authority()` refuse the read
 * whatever the client believes. What lives here is the same rule, so a surface
 * can hide a button rather than offering an action that fails. The database is
 * the enforcement; this is the courtesy. Same division as
 * `commissionAssignment.ts`.
 *
 * Two things are worth stating up front, because each reads as a bug until you
 * know why:
 *
 *  1. **The five are separate on purpose and must not be collapsed.** Approving
 *     and paying are held by different roles by default. A single
 *     `canManageCommission` helper would erase that in one line, so there is
 *     deliberately no such helper here.
 *  2. **Nothing falls back to a permissive default.** An unknown permission name
 *     or an absent set answers false.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * COM-R.170 · WHAT WAS REMOVED, AND WHY THE REST NOW HAS A SCREEN
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This module was written ahead of two screens that were never built, and its
 * copy was asserted in tests while being rendered nowhere. That is worse than
 * untested copy: the tests read as proof that the product explains something it
 * had never once said out loud. Found on the first sign-in of the unprivileged
 * rep login, 2026-08-27.
 *
 * `visibleScope`, `MANAGER_AMOUNTS_HIDDEN` and the `'team'` arm are GONE. They
 * served a manager view of a team's amounts, and `commission_manager_sees_rep_amounts`
 * appeared in this file and nowhere else in the client. `explainNoLines` is gone
 * too: `myEarnings.ts` `emptyState()` supersedes it and is better, distinguishing
 * "not asked yet" from "asked, and the answer is none" and naming blocked deals.
 *
 * What remains is rendered. `COMMISSION_PERMISSION_COPY` and
 * `CONCENTRATION_WARNING` are shown by `CommissionRolePermissionsNote`, under the
 * role picker on the user form, which is where a role is actually handed to a
 * person. `holdsCommissionPermission` is used by `commissionPayPeriods.ts`.
 *
 * ⚠️ If you add an export here, give it a caller in the same change. The whole
 * point of this note is that the last six did not have one.
 */

// ─────────────────────────────────────────────────────────────────────────────
// The five
// ─────────────────────────────────────────────────────────────────────────────

export const COMMISSION_PERMISSIONS = [
  'commission.view',
  'commission.approve',
  'commission.pay',
  'commission.export',
  'commission.adjust',
] as const;

export type CommissionPermission = (typeof COMMISSION_PERMISSIONS)[number];

/**
 * What each permission licenses, in the words the settings screen shows.
 *
 * ⚠️ This copy is load-bearing and is asserted in `commissionPermissions.test.ts`.
 * It is the only place the product explains that approving and paying are
 * different authorities, so a tidy-up of "just wording" should fail a test.
 */
export const COMMISSION_PERMISSION_COPY: Record<
  CommissionPermission,
  { label: string; explanation: string }
> = {
  'commission.view': {
    label: "See everyone's commission",
    explanation:
      'See commission lines across the account. Without it a person still sees their own lines, because being a contributor is not a permission.',
  },
  'commission.approve': {
    label: 'Approve commission',
    explanation:
      'Move a line or batch out of pending. This is not the authority to pay it.',
  },
  'commission.pay': {
    label: 'Mark commission paid',
    explanation:
      'Mark an approved line or batch paid. Held separately from approval so that one person cannot approve their own payment.',
  },
  'commission.export': {
    label: 'Export commission',
    explanation:
      'Take commission lines, batches or statements out of the product.',
  },
  'commission.adjust': {
    label: 'Adjust commission',
    explanation:
      'Adjust, reverse or void a line. The only authority that can change a calculated amount.',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Holding a permission
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `system.override` satisfies every one of the five, mirroring
 * `commission_holds_permission()`. Superadmin is granted none of the five
 * directly, so a client that only looked for the five would hide every money
 * action from the one role that certainly has it.
 */
export const SYSTEM_OVERRIDE = 'system.override';

export function holdsCommissionPermission(
  held: readonly string[] | null | undefined,
  permission: CommissionPermission,
): boolean {
  if (!held || held.length === 0) return false;
  return held.includes(permission) || held.includes(SYSTEM_OVERRIDE);
}

/**
 * The concentration rule as the settings screen states it.
 *
 * ⚠️ Mirrors `commission_permission_concentration()`, which is the ratchet that
 * actually enforces it. Roles carrying `system.override` are excluded for the
 * same reason: for them the five are already redundant.
 */
export function concentratesAllFive(held: readonly string[] | null | undefined): boolean {
  if (!held || held.includes(SYSTEM_OVERRIDE)) return false;
  return COMMISSION_PERMISSIONS.every((p) => held.includes(p));
}

export const CONCENTRATION_WARNING =
  'This role would hold all five money permissions. Approving, paying and adjusting are separated so that no one person can move money end to end.';

// ─────────────────────────────────────────────────────────────────────────────
// What a role actually grants, for the screen that grants it
// ─────────────────────────────────────────────────────────────────────────────

/**
 * COM-R.170. Which of the five a set of permission names contains, in the order
 * the copy above declares them, so a screen can list them predictably.
 *
 * ⚠️ `system.override` DELIBERATELY DOES NOT EXPAND HERE, and this is the one
 * place in the module where it must not.
 *
 * `holdsCommissionPermission()` treats override as satisfying any of the five,
 * because that is what `cost_holds_permission()` does and a screen deciding
 * whether to show a button has to agree with the database. This function answers
 * a different question: what has a role been GRANTED. Expanding override here
 * would tell an admin that Superadmin holds all five and trip the concentration
 * warning on the one role for which the warning is meaningless -- measured, role
 * 42 grants 76 permissions and NONE of them is one of the five.
 */
export function moneyPermissionsHeld(
  held: readonly string[] | null | undefined,
): CommissionPermission[] {
  if (!held || held.length === 0) return [];
  return COMMISSION_PERMISSIONS.filter((p) => held.includes(p));
}
